import { ElementType, forwardRef, ReactNode } from 'react';
import styled from 'styled-components';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../utils/polymorphic_component_prop_with_ref';
import { Icon, IconDictionary } from '../icon';
import { theme } from './text_link.theme';

const StyledLink = styled.a`
	all: unset;

	--font: ${theme.getValue('baseText')};
	--iconSize: ${theme.getValue('baseIconSize')};
	--iconMargin: 0;
	--iconMarginLeft: 0;
	--iconMarginRight: 0;
	--iconOrder: 0;

	-webkit-tap-highlight-color: transparent;
	-webkit-user-drag: none;
	-webkit-touch-callout: none;

	display: inline-flex;
	color: ${theme.getValue('baseTextColor')};
	text-decoration: none;
	align-items: center;
	cursor: pointer;
	word-wrap: break-word;
	word-break: break-word;

	&:active {
		color: ${theme.getValue('activeTextColor')};
	}

	&:focus-visible {
		outline: 2px solid ${theme.getValue('baseFocusRingColor')};
		outline-offset: 3px;
		border-radius: 1px;
	}

	&[data-size='small'] {
		--font: ${theme.getValue('smallText')};
		--iconSize: ${theme.getValue('smallIconSize')};
		--iconMargin: ${theme.getValue('smallIconMargin')};
	}

	&[data-size='medium'] {
		--font: ${theme.getValue('mediumText')};
		--iconSize: ${theme.getValue('mediumIconSize')};
		--iconMargin: ${theme.getValue('mediumIconMargin')};
	}

	&[data-size='large'] {
		--font: ${theme.getValue('largeText')};
		--iconSize: ${theme.getValue('largeIconSize')};
		--iconMargin: ${theme.getValue('largeIconMargin')};
	}

	&[data-icon-position='leading'] {
		--iconMarginLeft: 0;
		--iconMarginRight: var(--iconMargin);
		--iconOrder: 0;
	}

	&[data-icon-position='trailing'] {
		--iconMarginLeft: var(--iconMargin);
		--iconMarginRight: 0;
		--iconOrder: 2;
	}

	@media (hover: hover) {
		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:hover {
			text-decoration: underline;
		}
	}
`;

const StyledText = styled.span`
	font: var(--font);
`;

const StyledIcon = styled(Icon)`
	flex: 0 0 auto;
	width: var(--iconSize);
	height: var(--iconSize);
	margin-left: var(--iconMarginLeft);
	margin-right: var(--iconMarginRight);
	order: var(--iconOrder);
`;

type TextLinkSize = 'small' | 'medium' | 'large';
type TextLinkIconPosition = 'leading' | 'trailing';

type TextLinkProps<C extends ElementType> = PolymorphicComponentPropWithRef<
	C,
	{
		/**
		 * The content in the link.
		 */
		children: ReactNode;

		/**
		 * The visual size of the link.
		 *
		 * @default medium
		 */
		size?: TextLinkSize;

		/**
		 * Use to add a icon in the button. The position of the icon can be controlled
		 * with `iconPosition`-property.
		 */
		icon?: keyof IconDictionary;

		/**
		 * Specifies the position of the icon. Leading or trailing. It has only effect
		 * in combination with `icon`-property.
		 *
		 * @default leading
		 */
		iconPosition?: TextLinkIconPosition;
	}
>;

function CellulaTextLink<C extends ElementType = 'a'>(
	{ children, size = 'medium', icon, iconPosition = 'leading', as, ...rest }: TextLinkProps<C>,
	ref: PolymorphicRef<C>,
): JSX.Element {
	return (
		<StyledLink as={as} {...rest} data-size={size} data-icon-position={icon ? iconPosition : undefined} ref={ref}>
			{icon ? <StyledIcon type={icon} /> : null}
			<StyledText>{children}</StyledText>
		</StyledLink>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to allow the use of normal links.
 */
export const TextLink = forwardRef(
	CellulaTextLink,
) as typeof CellulaTextLink; /* This cast is necessary to unwrap the intrinsic types of the polymorphed component */
export type { TextLinkProps };
